import { useCallback, useState } from 'react'

import { DEFAULT_SETTINGS, QUEUE_DASHBOARD_SETTINGS_KEY } from '../constants'
import type { QueueDashboardSettings } from '../types'

const parseStoredSettings = (
  stored: string | null,
): QueueDashboardSettings | null => {
  if (!stored) return null

  try {
    const parsed = JSON.parse(stored)

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.refreshInterval === 'number' &&
      typeof parsed.holdAlertThreshold === 'number' &&
      typeof parsed.sampleHistoryMinutes === 'number'
    ) {
      return parsed as QueueDashboardSettings
    }

    return null
  } catch {
    return null
  }
}

const loadSettingsFromStorage = (): QueueDashboardSettings => {
  const stored = sessionStorage.getItem(QUEUE_DASHBOARD_SETTINGS_KEY)
  const parsed = parseStoredSettings(stored)
  return parsed ?? DEFAULT_SETTINGS
}

const saveSettingsToStorage = (settings: QueueDashboardSettings): void => {
  sessionStorage.setItem(QUEUE_DASHBOARD_SETTINGS_KEY, JSON.stringify(settings))
}

export interface UseQueueSettingsReturn {
  settings: QueueDashboardSettings
  updateSettings: (updates: Partial<QueueDashboardSettings>) => void
  resetSettings: () => void
}

export const useQueueSettings = (): UseQueueSettingsReturn => {
  const [settings, setSettings] = useState<QueueDashboardSettings>(
    loadSettingsFromStorage,
  )

  const updateSettings = useCallback(
    (updates: Partial<QueueDashboardSettings>) => {
      setSettings((prev) => {
        const newSettings = { ...prev, ...updates }
        saveSettingsToStorage(newSettings)
        return newSettings
      })
    },
    [],
  )

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS)
    saveSettingsToStorage(DEFAULT_SETTINGS)
  }, [])

  return { settings, updateSettings, resetSettings }
}

export default useQueueSettings
